// src/modules/app-views/crm/pages/Kanban/modals/EditOpportunityModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import Step2 from '../NewOpportunityModal/Step2';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN MODAL COMPONENT
// *******************************************************************************
const CrmEditOpportunityModal = ({ show, setShow, afterClose, initialValues, crmData, card, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // crmData has entityId + personContactId needed for entity update on save
  const crmDataRef = useRef(crmData);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    const values = form.getFieldsValue(true);
    const data = crmDataRef.current;
    const isCompany = card?.__entity?.entityType === 'company';

    // 1. Update CRM record fields
    await axiosService.put({
      url: API.CRM.URL.replace(':_id?', card._id),
      data: {
        opportunity: (values.opportunity || '').trim(),
        opportunityType: values.opportunityType,
        note: (values.note || '').trim() || null,
        consultantId: values.consultantId || null,
      },
      returnAsObject: true,
    });

    // 2. Update person entity (person contact when company, or the person entity itself)
    const personEntityId = isCompany ? data?.personContactId : data?.entityId;
    if (personEntityId) {
      const personPayload = { name: (values.name || '').trim() };

      if (values.phone?.value?.trim()) {
        personPayload.phone = {
          value: values.phone.value.trim(),
          countryCode: values.phone.countryCode || 'BR',
          type: values.phone.type || 'mobile',
        };
      }

      if (values.email?.value?.trim()) {
        personPayload.email = {
          value: values.email.value.trim(),
          countryCode: 'BR',
        };
      }

      if (values.jobTitleRaw?.trim()) {
        personPayload.jobTitleRaw = values.jobTitleRaw.trim();
      }

      await axiosService.put({
        url: API.ENTITIES.URL.replace(':_id?', personEntityId),
        data: personPayload,
        returnAsObject: true,
      });
    }

    // 3. Update company entity with businessAreaRaw (only when entity is company)
    if (isCompany && values.businessAreaRaw?.trim() && data?.entityId) {
      await axiosService.put({
        url: API.ENTITIES.URL.replace(':_id?', data.entityId),
        data: { businessAreaRaw: values.businessAreaRaw.trim() },
        returnAsObject: true,
      });
    }

    window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));
    appService.message({ flag: 's', text: 'saved_successfully' });
    onSuccess?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      {
        stepTitle: 'edit_opportunity',
        content: () => <Step2 />,
      },
    ],
    []
  );

  const requiredFields = ['name', 'opportunity', 'opportunityType'];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      title={translateX('edit_opportunity')}
      icon={<EditOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      requiredFields={requiredFields}
      width={540}
    />
  );
};

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showKanbanEditModal({ card, onSuccess } = {}) {
  // Fetch CRM record first so the modal opens already populated
  axiosService
    .get({ url: API.CRM.URL.replace(':_id?', card._id), returnAsObject: true })
    .then(([crmData]) => {
      if (!crmData) return;

      const isCompany = card?.__entity?.entityType === 'company';

      const personName = isCompany
        ? card.__personContact?.name || card.__personContact?.firstName || ''
        : card.__entity?.name || card.__entity?.firstName || '';

      const companyName = isCompany ? card.__entity?.name || '' : '';

      const phoneValue = isCompany
        ? card.__personContact?.phone?.value
        : card.__entity?.phone?.value;

      const emailValue = isCompany
        ? card.__personContact?.email?.value
        : card.__entity?.email?.value;

      const initialValues = {
        name: personName,
        company: companyName,
        phone: phoneValue ? { value: phoneValue } : undefined,
        email: emailValue ? { value: emailValue } : undefined,
        opportunity: crmData.opportunity || '',
        opportunityType: crmData.opportunityType || 'sale',
        note: crmData.note || '',
        consultantId: crmData.consultantId || undefined,
      };

      const container = document.createElement('div');
      container.id = 'kanban-edit-modal-container';
      document.body.appendChild(container);

      const root = createRoot(container);
      let isModalOpen = true;

      const renderModal = () => {
        root.render(
          <ThemeWrapper>
            <CrmEditOpportunityModal
              show={isModalOpen}
              setShow={handleClose}
              afterClose={handleAfterClose}
              initialValues={initialValues}
              crmData={crmData}
              card={card}
              onSuccess={onSuccess}
            />
          </ThemeWrapper>
        );
      };

      const handleClose = () => {
        isModalOpen = false;
        renderModal();
      };

      const handleAfterClose = () => {
        setTimeout(() => {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }, 0);
      };

      renderModal();
    });
}

export default showKanbanEditModal;
